import React, { useEffect, useRef } from "react"
import { Grid, Typography } from "@mui/material"
import { LoadingButton, Modal, useCalendarEvents, useMeetings } from "@tellescope/react-components"
import { CalendarEvent, Meeting } from "@tellescope/types-client"
import { ControlBar, LocalPreview, useCurrentCallContext, useJoinMeeting, VideoTileGrid } from "@tellescope/video-chat"
import { Navigate, useSearchParams } from "react-router-dom"
import { routes, useNavigateToPage } from "../definitions/routes"
import { useFullHeight } from "../components/navigation"

interface CallHandlers {
  onLeave?: () => void;
  onStart?: () => void;
}
interface VideoCallProps extends CallHandlers {}
export const VideoCallView = ({ ...props } : VideoCallProps) => (
  <Modal open={true} setOpen={() => undefined} 
    style={{ width: '100vw', height: '100vh' }}
  >
  <Grid container direction="column" id="video-container">
    <Grid container sx={{ 
      height: (
        (
          window.navigator.userAgent.toLowerCase().includes('iphone')
        || window.navigator.userAgent.toLowerCase().includes('ipad')
        || window.navigator.userAgent.toLowerCase().includes('ipod')
        )
          ? `calc(100% - 175px)`
          : `calc(100% - 70px)`
      )
    }}>
      <VideoTileGrid />
    </Grid>
    
    <ControlBar {...props} 
      style={{ position: 'fixed', bottom: 0, width: '100%', backgroundColor: 'black' }}
    />
  </Grid>
  </Modal>
)

const Instruction = ({ number, content } : { number: string, content: React.ReactNode }) => (
  <Grid container alignItems="center" wrap="nowrap" spacing={1}>
    <Grid item>
    <Grid container alignItems={"center"} justifyContent={"center"}
       sx={{ width: 40, height: 40, borderRadius: '100%', backgroundColor: 'primary.main' }}
    >
      <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 25 }}>
        {number}
      </Typography>
    </Grid>
    </Grid>

    <Grid item>
    {typeof content === 'string'
      ? <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>
          {content}
        </Typography>
      : content
    }
    </Grid>
  </Grid>
)

export const WaitingRoom = ({ calendarEvent, meeting } : { calendarEvent?: CalendarEvent, meeting?: Meeting }) => {
  const navigate = useNavigateToPage()
  const height = useFullHeight()
  const [, { findById: findEvent }] = useCalendarEvents()
  const [, { findById: findMeeting }] = useMeetings()
  const { joinMeeting } = useJoinMeeting()
  const startedRef = useRef(meeting)
  const alertRef = useRef(false)

  // poll to check for started meeting
  useEffect(() => { 
    const t = setInterval(() => {
      if (!calendarEvent?.id) return

      const event = findEvent(calendarEvent.id, { reload: true })
      findMeeting(event?.meetingId ?? '', { reload: true }) // reloads tsMeeting
    }, 2500)
    
    return () => { clearInterval(t) }
  }, [calendarEvent, findMeeting, findEvent])

  // when there's a Zoom call, don't load the built-in video conferencing or show preview
  useEffect(() => {
    if (calendarEvent?.videoIntegration && calendarEvent.videoIntegration !== 'No Integration') {
      navigate(routes.events, { id: calendarEvent.id })
    }
  }, [calendarEvent, navigate])

  if (!startedRef.current && meeting && !alertRef.current) {
    alertRef.current = true // don't alert again
    startedRef.current = meeting
    alert("The meeting has started. Please click 'Join Meeting' to join.")
  }

  return (
    <Grid container direction="column" alignItems="center" justifyContent="center"
      style={{ minHeight: height, width: '100%'}}
    >
      <Typography style={{ fontSize: 20, fontWeight: 'bold' }}>
        {calendarEvent?.title}
      </Typography>

      <Typography style={{ marginBottom: 15 }}>
        Waiting Room
      </Typography>

      {/* don't show and prompt for permissions if using external video url */}
      {calendarEvent && (!calendarEvent.videoIntegration || calendarEvent.videoIntegration === 'No Integration') 
        && <LocalPreview />
      }

      <LoadingButton variant="contained" disabled={!meeting || meeting.status === 'ended'} 
        onClick={() => joinMeeting(meeting!)} 
        submitText="Join Meeting" submittingText="Join"
        style={{ marginTop: 15 }} 
      />

      <Typography style={{ marginTop: 5 }}>
        {!meeting 
          ? "Waiting for the host to start the meeting" 
          : meeting?.status === 'ended'
           ? "This meeting has been ended by the host"
           : ''
        }

      </Typography>

      <Grid container direction="column" sx={{ width: '100%', maxWidth: 525, mt: 1.5 }} spacing={1}>
        <Grid item>
          <Instruction number="1" content={"Make sure your video and audio permissions are enabled"} />
        </Grid>

        <Grid item>
          <Instruction number="2" content={`Once the meeting has started, press "Join Meeting" to join`} />
        </Grid>

        <Grid item>
          <Instruction number="3" 
            content={`If it's past your start time, you can try refreshing this page every few minutes`} 
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

export const VideoCall = () => {
  const navigate = useNavigateToPage()
  const [search] = useSearchParams()

  const [, { findById: findMeeting }] = useMeetings()
  const [, { findById: findEvent }] = useCalendarEvents()

  const { meeting } = useCurrentCallContext()

  const calendarEventId = search.get('calendarEventId')
  const meetingId = search.get('meetingId')

  const event = findEvent(calendarEventId ?? '')
  const tsMeeting = findMeeting(meetingId ?? event?.meetingId ?? '') 

  if (meeting) {
    return (
      <VideoCallView onLeave={() => { navigate(routes.home) }} />
    )
  }
  if (event === null) {
    return <Typography>This event no longer exists</Typography>
  }
  if (calendarEventId || meetingId) {
    return (
      <WaitingRoom calendarEvent={event} meeting={tsMeeting ?? undefined} />
    )
  }
  return <Navigate replace to={routes.home}/> 
}